import {useEffect, useState } from "react";
import {PageStandard} from "../utils/PageStandard";
import {AwardModel, UserModel } from "../utils/Model";
import {addAwards, deleteAward, getAwards, getUsers, updateAward} from "../utils/ViewModel";
import {Autocomplete, Button, Chip, DialogContent, DialogTitle, FormControl,
    FormLabel, IconButton, Input, Modal, ModalClose, ModalDialog, Stack, Table, Typography,
    createFilterOptions} from "@mui/joy";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";


export function Awards() {

    const initialForm = {
        title: "",
        description: "",
        valid_until: "",
    }

    const [users, setUsers] = useState<UserModel[]>([]);
    const [awards, setAwards] = useState<AwardModel[]>([]);
    const [modalAddAward, setModalAddAward] = useState(false);
    const [loading, setLoading] = useState(false);
    const [newAward, setNewAward] = useState(initialForm);
    const [recipients, setRecipients] = useState<UserModel[]>([]);
    const filter = createFilterOptions<UserModel>();

    const [editingAward, setEditingAward] = useState<AwardModel | null>(null);
    const [editForm, setEditForm] = useState(initialForm);
    const [editLoading, setEditLoading] = useState(false);

    const refreshAwards = () => getAwards().then(setAwards).catch(() => setAwards([]));

    useEffect(() => {
        getUsers()
            .then(it => setUsers(it))
            .catch(() => setUsers([]));
        refreshAwards();
    }, []);

    const handleDeleteAward = (id: string, refresh?: () => void) => {
        deleteAward(id)
            .then((ok) => {
                if (ok && refresh) refresh();
            })
            .catch((err) => alert("Errore durante l'eliminazione del premio: " + err.message));
    };

    /** Awards status shown in the table: usato / scaduto / disponibile. */
    const statusOf = (a: AwardModel): { label: string; color: "success" | "neutral" | "danger" } => {
        if (a.used) return { label: "Usato", color: "neutral" };
        if (a.valid_until && new Date(a.valid_until) < new Date()) return { label: "Scaduto", color: "danger" };
        return { label: "Disponibile", color: "success" };
    };

    const toDateInputValue = (iso: string | null | undefined) => iso ? iso.slice(0, 10) : "";

    const openEditAward = (a: AwardModel) => {
        setEditingAward(a);
        setEditForm({
            title: a.title,
            description: a.description,
            valid_until: toDateInputValue(a.valid_until),
        });
    };

    const handleUpdateAward = () => {
        if (!editingAward) return;
        setEditLoading(true);
        updateAward(editingAward.id, editForm.title, editForm.description, editForm.valid_until)
            .then((ok) => {
                if (ok) {
                    setEditingAward(null);
                    refreshAwards();
                }
            })
            .finally(() => setEditLoading(false));
    };

    if (!users || !awards) return null;
    return (
        <PageStandard title="Awards" sidebarVisible={true} headerEnd={<Button onClick={() => setModalAddAward(true)}>Aggiungi premio</Button>}>
            <Table aria-label="Utenti" size="md" stickyHeader>
                <thead>
                <tr>
                    <th style={{ width: "30%" }}>Title</th>
                    <th style={{ width: "20%" }}>User</th>
                    <th style={{ width: "20%" }}>Email</th>
                    <th style={{ width: "10%" }}>Stato</th>
                    <th style={{ width: "auto" }}></th>
                </tr>
                </thead>
                <tbody>
                {awards.map((a) => {
                    const user = users.find(u => u.id === a.user_id)
                    if(!user) return null;
                    const status = statusOf(a);
                    return (
                        <tr key={a.id}>
                            <td>
                                <Typography level="body-sm">{a.title}</Typography>
                            </td>
                            <td>
                                <Typography level="body-sm">{user.firstname} {user.lastname}</Typography>
                            </td>
                            <td>
                                <Typography level="body-sm">{user.email}</Typography>
                            </td>
                            <td>
                                <Chip size="sm" color={status.color} variant="soft">{status.label}</Chip>
                            </td>
                            <td>
                                <Stack direction="row" spacing={1}>
                                    <IconButton variant="outlined" color="neutral" onClick={() => openEditAward(a)}>
                                        <Edit />
                                    </IconButton>
                                    <IconButton variant="outlined" color="danger" onClick={() => {
                                        if(window.confirm("Eliminare questo premio?")) {
                                            handleDeleteAward(a.id, refreshAwards)
                                        }
                                    }}>
                                        <Delete />
                                    </IconButton>
                                </Stack>
                            </td>
                        </tr>
                    )
                })}
                </tbody>
            </Table>

            <Modal open={modalAddAward} onClose={() => setModalAddAward(false)}>
                <ModalDialog aria-labelledby="user-details-title" layout="center" size="lg">
                    <ModalClose />
                    <DialogTitle id="user-details-title">Aggiungi premio</DialogTitle>
                    <DialogContent sx={{minWidth: "50vw"}}>
                        <Stack direction="column" spacing={2}>
                            <FormControl size="sm" sx={{ mb: 1 }}>
                                <FormLabel>Titolo</FormLabel>
                                <Input type="text"
                                       value={newAward.title}
                                       onChange={(e) => setNewAward({...newAward, title: e.target.value})}
                                />
                            </FormControl>
                            <FormControl size="sm" sx={{ mb: 1 }}>
                                <FormLabel>Descrizione</FormLabel>
                                <Input type="text"
                                       value={newAward.description}
                                       onChange={(e) => setNewAward({...newAward, description: e.target.value})}
                                />
                            </FormControl>
                            <FormControl size="sm" sx={{ mb: 1 }}>
                                <FormLabel>Data di scadenza</FormLabel>
                                <Input type="date"
                                       value={newAward.valid_until}
                                       onChange={(e) => setNewAward({...newAward, valid_until: e.target.value})}
                                />
                            </FormControl>
                            <FormControl size="sm" sx={{ mb: 1 }}>
                                <FormLabel>Utenti destinatari ({recipients.length})</FormLabel>
                                <Autocomplete
                                    multiple
                                    placeholder="Seleziona uno o più utenti"
                                    options={users}
                                    getOptionLabel={(option) =>
                                        `${option.firstname} ${option.lastname} (${option.email})`
                                    }
                                    value={recipients}
                                    onChange={(_, newValue) => setRecipients(newValue)}
                                    filterOptions={(options, params) => filter(options, params)}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.id}>
                                            {option.firstname} {option.lastname} – {option.email}
                                        </li>
                                    )}
                                />
                            </FormControl>
                            <Button disabled={ recipients.length === 0 || newAward.title === "" || newAward.description === "" || loading }
                                    onClick={() => {
                                        setLoading(true)
                                        const recipientIds = recipients.map(u => u.id)
                                        addAwards( recipientIds, newAward.title, newAward.description, newAward.valid_until )
                                            .then(() => {
                                                alert(recipientIds.length > 1 ? "Premi aggiunti" : "Premio aggiunto")
                                                setModalAddAward(false)
                                                setNewAward(initialForm)
                                                setRecipients([])
                                                refreshAwards()
                                            })
                                            .catch((err: any) => alert("Errore durante la creazione: " + err.message))
                                            .finally(() => setLoading(false))
                                    }}
                            >
                                Aggiungi
                            </Button>
                        </Stack>
                    </DialogContent>
                </ModalDialog>
            </Modal>

            <Modal open={!!editingAward} onClose={() => setEditingAward(null)}>
                <ModalDialog aria-labelledby="edit-award-title" layout="center" size="lg">
                    <ModalClose />
                    <DialogTitle id="edit-award-title">Modifica premio</DialogTitle>
                    <DialogContent sx={{minWidth: "50vw"}}>
                        <Stack direction="column" spacing={2}>
                            <FormControl size="sm" sx={{ mb: 1 }}>
                                <FormLabel>Titolo</FormLabel>
                                <Input type="text"
                                       value={editForm.title}
                                       onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                                />
                            </FormControl>
                            <FormControl size="sm" sx={{ mb: 1 }}>
                                <FormLabel>Descrizione</FormLabel>
                                <Input type="text"
                                       value={editForm.description}
                                       onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                                />
                            </FormControl>
                            <FormControl size="sm" sx={{ mb: 1 }}>
                                <FormLabel>Data di scadenza</FormLabel>
                                <Input type="date"
                                       value={editForm.valid_until}
                                       onChange={(e) => setEditForm({...editForm, valid_until: e.target.value})}
                                />
                            </FormControl>
                            <Button disabled={ editForm.title === "" || editForm.description === "" || editLoading }
                                    onClick={handleUpdateAward}
                            >
                                Salva
                            </Button>
                        </Stack>
                    </DialogContent>
                </ModalDialog>
            </Modal>
        </PageStandard>
    )
}